import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch, getAccessToken, ApiError } from '../../lib/api-client'
import { ComplianceDisclaimer } from '../../components/ComplianceDisclaimer'

// ─── Constants ────────────────────────────────────────────────────────────────

const BLUE = '#4f46e5'
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8000'

const inputSt: React.CSSProperties = {
  width: '100%',
  padding: '8px 11px',
  border: '1px solid #e8eaed',
  borderRadius: 7,
  fontSize: 13,
  color: '#111827',
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'inherit',
}

const CONDITIONS = [
  { value: 'ny',        label: 'Ny',        color: '#10b981' },
  { value: 'god',       label: 'God',       color: '#059669' },
  { value: 'slid',      label: 'Let slid',  color: '#f59e0b' },
  { value: 'forringet', label: 'Forringet', color: '#ef4444' },
  { value: 'mangler',   label: 'Mangler',   color: '#991b1b' },
]

const METER_TYPES = ['Vand', 'Varme', 'El', 'Gas', 'Varmtvand', 'Andet']
const KEY_TYPES   = ['Hovednøgle', 'Postkassenøgle', 'Adgangskort', 'Brikke', 'Andet']

// ─── Types ────────────────────────────────────────────────────────────────────

interface Photo { id: string; url: string }

interface Report {
  id: string
  type: 'indflytning' | 'fraflytning'
  status: string
  inspection_date: string | null
  inspection_started_at: string | null
  syn_indkaldt_dato?: string | null
  syn_afholdt_dato?: string | null
  rapport_leveret_dato?: string | null
  rep_first_name: string | null
  rep_last_name: string | null
  renter_first_name: string | null
  renter_last_name: string | null
  renter_email: string | null
  renter_phone: string | null
  renter_present: string
  period_14_day: boolean
  period_start_date: string | null
  renter_new_address: string | null
  renter_bank_reg: string | null
  renter_bank_account: string | null
  own_lease_number: string | null
  extra_notes: string | null
  property_address: string | null
  property_city: string | null
  rooms: Room[]
  meters: Meter[]
  keys: Key[]
}

interface Room {
  id: string
  name: string
  sort_order: number
  completed: boolean
  remark_count: number
  item_count: number
  rated_count: number
}

interface Item {
  id: string
  name: string
  condition: string | null
  sort_order: number
  remarks: Remark[]
}

interface Remark {
  id: string
  description: string
  remediation: string | null
  who_pays: string | null
  photos: Photo[]
}

interface Meter {
  id: string
  meter_type: string
  meter_number: string | null
  reading: number | null
  photos: Photo[]
}

interface Key {
  id: string
  key_type: string
  key_code: string | null
  count: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function AuthImg({ src, style }: { src: string; style?: React.CSSProperties }) {
  const [blobUrl, setBlobUrl] = useState('')
  useEffect(() => {
    let alive = true
    const token = getAccessToken()
    fetch(`${API_BASE}${src}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.blob())
      .then(blob => { if (alive) setBlobUrl(URL.createObjectURL(blob)) })
    return () => { alive = false }
  }, [src])
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl) }, [blobUrl])
  if (!blobUrl) return <div style={{ ...style, background: '#f3f4f6' }} />
  return <img src={blobUrl} style={{ ...style, objectFit: 'cover' }} />
}

async function uploadPhotoRaw(
  reportId: string,
  file: File,
  opts: { remarkId?: string; meterId?: string },
): Promise<Photo> {
  const form = new FormData()
  form.append('file', file)
  const params = new URLSearchParams()
  if (opts.remarkId) params.set('remark_id', opts.remarkId)
  if (opts.meterId)  params.set('meter_id', opts.meterId)
  const token = getAccessToken()
  const resp = await fetch(
    `${API_BASE}/api/v1/inspection-reports/${reportId}/photos?${params}`,
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    },
  )
  return resp.json() as Promise<Photo>
}

function roomIcon(name: string): React.ReactNode {
  const n = name.toLowerCase()
  if (n.includes('køkken'))                                   return <KitchenIcon />
  if (n.includes('bad') || n.includes('wc') || n.includes('toilet')) return <BathIcon />
  if (n.includes('sove'))                                     return <BedIcon />
  if (n.includes('entré') || n.includes('entre') || n.includes('gang')) return <EntryIcon />
  return <HomeIcon />
}

function conditionDot(room: Room) {
  if (room.completed) return '#16a34a'
  if (room.rated_count > 0) return '#f59e0b'
  return '#d1d5db'
}

// ─── Main component ───────────────────────────────────────────────────────────

export function InspectionWizardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [report, setReport] = useState<Report | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState(0)
  const [saving, setSaving] = useState(false)
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null)

  const [synError, setSynError] = useState<string | null>(null)
  const [dateError, setDateError] = useState<string | null>(null)
  const [showAddRoom, setShowAddRoom] = useState(false)
  const [newRoomName, setNewRoomName] = useState('')
  const [showMeterPicker, setShowMeterPicker] = useState(false)
  const [showKeyPicker, setShowKeyPicker] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    const r = await apiFetch<Report>(`/api/v1/inspection-reports/${id}`)
    setReport(r)
    setLoading(false)
  }, [id])

  useEffect(() => { (async () => { await load() })() }, [load])

  const patch = async (data: Record<string, unknown>) => {
    if (!id) return
    setSaving(true)
    try {
      await apiFetch(`/api/v1/inspection-reports/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      setReport(prev => prev ? { ...prev, ...data } as Report : prev)
    } finally {
      setSaving(false)
    }
  }

  // Syn-dates are validated server-side (notice, order of dates); the Danish
  // 422 detail is shown inline and the value is not saved silently.
  const patchSyn = async (field: 'syn_indkaldt_dato' | 'syn_afholdt_dato' | 'rapport_leveret_dato', value: string) => {
    if ((report?.[field] ?? '') === value) return
    setSynError(null)
    try {
      await patch({ [field]: value || null })
    } catch (e) {
      setSynError(e instanceof ApiError ? e.message : 'Datoen kunne ikke gemmes. Prøv igen.')
    }
  }

  // Same pattern for the planned syn date: show the Danish server message inline and
  // put the previous saved value back in the (uncontrolled) input.
  const patchInspectionDate = async (input: HTMLInputElement) => {
    const value = input.value
    if ((report?.inspection_date ?? '') === value) return
    setDateError(null)
    try {
      await patch({ inspection_date: value || null })
    } catch (e) {
      input.value = report?.inspection_date ?? ''
      setDateError(e instanceof ApiError ? e.message : 'Datoen kunne ikke gemmes. Prøv igen.')
    }
  }

  const submitAddRoom = async () => {
    if (!id || !newRoomName.trim()) return
    await apiFetch(`/api/v1/inspection-reports/${id}/rooms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newRoomName.trim() }),
    })
    setShowAddRoom(false)
    setNewRoomName('')
    load()
  }

  const addMeterOfType = async (mtype: string) => {
    if (!id) return
    setShowMeterPicker(false)
    await apiFetch(`/api/v1/inspection-reports/${id}/meters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ meter_type: mtype }),
    })
    load()
  }

  const removeMeter = async (meterId: string) => {
    if (!id) return
    await apiFetch(`/api/v1/inspection-reports/${id}/meters/${meterId}`, { method: 'DELETE' })
    load()
  }

  const addKeyOfType = async (ktype: string) => {
    if (!id) return
    setShowKeyPicker(false)
    await apiFetch(`/api/v1/inspection-reports/${id}/keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key_type: ktype }),
    })
    load()
  }

  const removeKey = async (keyId: string) => {
    if (!id) return
    await apiFetch(`/api/v1/inspection-reports/${id}/keys/${keyId}`, { method: 'DELETE' })
    load()
  }

  if (loading || !report) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#f7f8fa',
        fontSize: 13, color: '#9ca3af',
      }}>
        Indlæser…
      </div>
    )
  }

  const typeLabel = report.type === 'indflytning' ? 'Indflytningsrapport' : 'Fraflytningsrapport'
  const TABS = report.type === 'fraflytning'
    ? ['Detaljer', 'Rum', 'Forbrug', 'Nøgler', 'Fraflytningsinfo', 'Underskriv & send']
    : ['Detaljer', 'Rum', 'Forbrug', 'Nøgler', 'Underskriv & send']
  const lastTab = TABS.length - 1

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      overflow: 'hidden', background: '#f7f8fa',
    }}>

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', height: 54,
        background: 'white',
        borderBottom: '1px solid #e8eaed',
        flexShrink: 0,
      }}>
        {/* Left: back + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 200 }}>
          <button
            onClick={() => navigate('/flyttesyn')}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 12, color: '#6b7280',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15,18 9,12 15,6" />
            </svg>
            Tilbage
          </button>
          <div style={{ width: 1, height: 16, background: '#e8eaed' }} />
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{typeLabel}</span>
            {(report.property_address || report.property_city) && (
              <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
                {report.property_address ?? ''}{report.property_city && `, ${report.property_city}`}
              </span>
            )}
          </div>
        </div>

        {/* Centre: tab bar */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0, height: '100%' }}>
          {TABS.map((t, i) => (
            <button
              key={i}
              onClick={() => { setActiveRoomId(null); setTab(i) }}
              style={{
                padding: '0 16px',
                height: '100%',
                fontSize: 13,
                fontWeight: tab === i ? 600 : 400,
                color: tab === i ? BLUE : '#6b7280',
                background: 'none',
                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                borderBottom: tab === i ? `3px solid ${BLUE}` : '3px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color .12s',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Right: saving indicator */}
        <div style={{ minWidth: 200, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          {saving && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Gemmer…</span>
          )}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          {/* ── Tab 0: Detaljer ── */}
          {tab === 0 && (
            <div>
              <SectionTitle>Detaljer</SectionTitle>

              <Card title="Datoer">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Field label="Dato for indbudt / oversendelse">
                    <input style={inputSt} type="date"
                      defaultValue={report.inspection_date ?? ''}
                      onBlur={e => patchInspectionDate(e.target)} />
                  </Field>
                  <Field label="Flyttesyn påbegyndt">
                    <input style={inputSt} type="datetime-local"
                      defaultValue={report.inspection_started_at?.slice(0, 16) ?? ''}
                      onBlur={e => patch({ inspection_started_at: e.target.value || null })} />
                  </Field>
                </div>
                {dateError && (
                  <p role="alert" style={{ fontSize: 12, color: '#b91c1c', margin: '12px 0 0', lineHeight: 1.5 }}>
                    {dateError}
                  </p>
                )}
              </Card>

              {report.type === 'fraflytning' && (
                <Card title="Fraflytningssyn – frister">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                    <Field label="Indkaldelse sendt til lejer">
                      <input style={inputSt} type="date"
                        defaultValue={report.syn_indkaldt_dato ?? ''}
                        onBlur={e => patchSyn('syn_indkaldt_dato', e.target.value)} />
                    </Field>
                    <Field label="Syn afholdt">
                      <input style={inputSt} type="date"
                        defaultValue={report.syn_afholdt_dato ?? ''}
                        onBlur={e => patchSyn('syn_afholdt_dato', e.target.value)} />
                    </Field>
                    <Field label="Rapport udleveret til lejer">
                      <input style={inputSt} type="date"
                        defaultValue={report.rapport_leveret_dato ?? ''}
                        onBlur={e => patchSyn('rapport_leveret_dato', e.target.value)} />
                    </Field>
                  </div>
                  {synError && (
                    <p role="alert" style={{ fontSize: 12, color: '#b91c1c', margin: '12px 0 0', lineHeight: 1.5 }}>
                      {synError}
                    </p>
                  )}
                </Card>
              )}

              <Card title="Udlejer / repræsentant">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Field label="Fornavn">
                    <input style={inputSt} defaultValue={report.rep_first_name ?? ''}
                      onBlur={e => patch({ rep_first_name: e.target.value || null })} />
                  </Field>
                  <Field label="Efternavn">
                    <input style={inputSt} defaultValue={report.rep_last_name ?? ''}
                      onBlur={e => patch({ rep_last_name: e.target.value || null })} />
                  </Field>
                </div>
              </Card>

              <Card title="Lejer">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Field label="Fornavn">
                    <input style={inputSt} defaultValue={report.renter_first_name ?? ''}
                      onBlur={e => patch({ renter_first_name: e.target.value || null })} />
                  </Field>
                  <Field label="Efternavn">
                    <input style={inputSt} defaultValue={report.renter_last_name ?? ''}
                      onBlur={e => patch({ renter_last_name: e.target.value || null })} />
                  </Field>
                  <Field label="E-mail">
                    <input style={inputSt} type="email" defaultValue={report.renter_email ?? ''}
                      onBlur={e => patch({ renter_email: e.target.value || null })} />
                  </Field>
                  <Field label="Telefon">
                    <input style={inputSt} type="tel" defaultValue={report.renter_phone ?? ''}
                      onBlur={e => patch({ renter_phone: e.target.value || null })} />
                  </Field>
                </div>
                <div style={{ marginTop: 6 }}>
                  <Field label="Er lejeren til stede ved flytsynet?">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                      {[
                        { v: 'ja',          l: 'Ja, lejeren er til stede' },
                        { v: 'nej',         l: 'Nej, lejeren er ikke til stede' },
                        { v: 'fuldmaegtig', l: 'Lejer repræsenteres af en fuldmægtig' },
                      ].map(opt => (
                        <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                          <input type="radio" name="renter_present"
                            checked={report.renter_present === opt.v}
                            onChange={() => { patch({ renter_present: opt.v }); setReport(r => r ? { ...r, renter_present: opt.v } : r) }} />
                          <span style={{ fontSize: 13, color: '#374151' }}>{opt.l}</span>
                        </label>
                      ))}
                    </div>
                  </Field>
                </div>
              </Card>

              {report.type === 'indflytning' && (
                <Card title="14-dages fejl- og mangelperiode">
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14, marginTop: 0 }}>
                    Indhent fejl og mangler fra din lejer indenfor den lovpligtige 14-dages periode efter indflytning.
                  </p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
                    <input type="checkbox" checked={report.period_14_day}
                      onChange={e => { patch({ period_14_day: e.target.checked }); setReport(r => r ? { ...r, period_14_day: e.target.checked } : r) }} />
                    <span style={{ fontSize: 13, color: '#374151' }}>Indhent fejl og mangler fra lejeren fremållet</span>
                  </label>
                  {report.period_14_day && (
                    <Field label="Indflytningsdato">
                      <input style={inputSt} type="date"
                        defaultValue={report.period_start_date ?? ''}
                        onBlur={e => patch({ period_start_date: e.target.value || null })} />
                    </Field>
                  )}
                </Card>
              )}

              <Card title="Ekstra noter">
                <Field label="Noter">
                  <textarea
                    style={{ ...inputSt, minHeight: 88, resize: 'vertical' }}
                    defaultValue={report.extra_notes ?? ''}
                    onBlur={e => patch({ extra_notes: e.target.value || null })}
                    placeholder="Tilføj eventuelle ekstra noter…"
                  />
                </Field>
              </Card>
            </div>
          )}

          {/* ── Tab 1: Rum — room list ── */}
          {tab === 1 && !activeRoomId && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <SectionTitle style={{ margin: 0 }}>Gennemgå rum</SectionTitle>
                <button
                  onClick={() => { setShowAddRoom(true); setNewRoomName('') }}
                  style={{
                    padding: '8px 16px', fontSize: 12, fontWeight: 600,
                    background: 'white', color: '#374151',
                    border: '1px solid #e8eaed', borderRadius: 8, cursor: 'pointer',
                  }}
                >
                  + Tilføj rum
                </button>
              </div>

              <div style={{
                background: '#eef2ff',
                border: '1px solid #c7d2fe',
                borderRadius: 8,
                padding: '11px 16px',
                fontSize: 13,
                color: '#3730a3',
                marginBottom: 18,
              }}>
                Gå rum for rum, vurder standen af de enkelte overflader og tilføj anmærkninger med dokumentation hvor nødvendigt.
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {report.rooms.map(room => (
                  <div
                    key={room.id}
                    onClick={() => setActiveRoomId(room.id)}
                    style={{
                      background: 'white',
                      border: '1px solid #e8eaed',
                      borderRadius: 10,
                      padding: '13px 18px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      transition: 'border-color .12s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = BLUE)}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#e8eaed')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 8,
                        background: '#f7f8fa', border: '1px solid #e8eaed',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {roomIcon(room.name)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{room.name}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>
                          {room.remark_count > 0
                            ? `${room.remark_count} anmærkning${room.remark_count !== 1 ? 'er' : ''}`
                            : 'Ingen anmærkninger'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {/* condition dot */}
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: conditionDot(room),
                      }} />
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>
                        {room.rated_count}/{room.item_count}
                      </span>
                      {room.completed && (
                        <span style={{
                          fontSize: 11, color: '#16a34a', fontWeight: 600,
                          background: '#f0fdf4', padding: '2px 8px', borderRadius: 99,
                        }}>
                          Fuldendt
                        </span>
                      )}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9,18 15,12 9,6" />
                      </svg>
                    </div>
                  </div>
                ))}

                {showAddRoom && (
                  <div style={{
                    background: 'white',
                    border: `1.5px solid ${BLUE}`,
                    borderRadius: 10,
                    padding: '12px 16px',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}>
                    <input
                      style={{ ...inputSt, flex: 1 }}
                      value={newRoomName}
                      onChange={e => setNewRoomName(e.target.value)}
                      placeholder="Rum navn, fx Kontor"
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') submitAddRoom() }}
                    />
                    <button
                      onClick={submitAddRoom}
                      style={{
                        padding: '8px 16px', background: BLUE, color: 'white',
                        border: 'none', borderRadius: 7, cursor: 'pointer',
                        fontWeight: 600, fontSize: 13,
                      }}
                    >
                      Tilføj
                    </button>
                    <button
                      onClick={() => { setShowAddRoom(false); setNewRoomName('') }}
                      style={{
                        padding: '8px 10px', background: 'white', border: '1px solid #e8eaed',
                        borderRadius: 7, cursor: 'pointer', fontSize: 14, color: '#9ca3af',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {!showAddRoom && (
                  <button
                    onClick={() => { setShowAddRoom(true); setNewRoomName('') }}
                    style={{
                      marginTop: 4,
                      padding: '10px 0',
                      fontSize: 13,
                      color: '#9ca3af',
                      background: 'none',
                      border: '1.5px dashed #e8eaed',
                      borderRadius: 8,
                      cursor: 'pointer',
                      width: '100%',
                    }}
                  >
                    + Tilføj rum
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Tab 1: Rum — room detail ── */}
          {tab === 1 && activeRoomId && (
            <RoomDetailView
              reportId={id!}
              roomId={activeRoomId}
              room={report.rooms.find(r => r.id === activeRoomId)!}
              onBack={() => { setActiveRoomId(null); load() }}
            />
          )}

          {/* ── Tab 2: Forbrug ── */}
          {tab === 2 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <SectionTitle style={{ margin: 0 }}>Forbrugsmålinger</SectionTitle>
                <button
                  onClick={() => setShowMeterPicker(true)}
                  style={{
                    padding: '8px 16px', fontSize: 12, fontWeight: 600,
                    background: BLUE, color: 'white',
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                  }}
                >
                  + Tilføj måler
                </button>
              </div>

              <div style={{
                background: '#f7f8fa',
                border: '1px solid #e8eaed',
                borderRadius: 8,
                padding: '11px 16px',
                fontSize: 13,
                color: '#6b7280',
                marginBottom: 18,
              }}>
                Aflæs og noter forbrugsmålinger — vand, varme, el og lignende.
              </div>

              {report.meters.length === 0 ? (
                <div style={{
                  border: '1.5px dashed #e8eaed',
                  borderRadius: 10,
                  padding: '40px 24px',
                  textAlign: 'center',
                }}>
                  <button
                    onClick={() => setShowMeterPicker(true)}
                    style={{
                      fontSize: 13, color: '#6b7280', background: 'none',
                      border: '1px solid #e8eaed', borderRadius: 8,
                      padding: '10px 20px', cursor: 'pointer',
                    }}
                  >
                    + Tilføj forbrugstype
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {report.meters.map(m => (
                    <MeterRow
                      key={m.id}
                      meter={m}
                      reportId={id!}
                      onDelete={() => removeMeter(m.id)}
                      onUpdate={load}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Tab 3: Nøgler ── */}
          {tab === 3 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <SectionTitle style={{ margin: 0 }}>Nøgleindlevering</SectionTitle>
                <button
                  onClick={() => setShowKeyPicker(true)}
                  style={{
                    padding: '8px 16px', fontSize: 12, fontWeight: 600,
                    background: BLUE, color: 'white',
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                  }}
                >
                  + Tilføj nøgle
                </button>
              </div>

              <div style={{
                background: '#f7f8fa',
                border: '1px solid #e8eaed',
                borderRadius: 8,
                padding: '11px 16px',
                marginBottom: 18,
              }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>
                  Sådan håndteres nøgleindlevering
                </p>
                <ol style={{ paddingLeft: 18, margin: 0, color: '#6b7280', fontSize: 13, lineHeight: '1.7' }}>
                  <li>Tilføj og vælg en indleveringstype og tilføj evt. et navn/kode</li>
                  <li>Vælg hvor mange eksemplarer der indleveres</li>
                </ol>
              </div>

              {report.keys.length === 0 ? (
                <div style={{
                  border: '1.5px dashed #e8eaed',
                  borderRadius: 10,
                  padding: '40px 24px',
                  textAlign: 'center',
                }}>
                  <button
                    onClick={() => setShowKeyPicker(true)}
                    style={{
                      fontSize: 13, color: '#6b7280', background: 'none',
                      border: '1px solid #e8eaed', borderRadius: 8,
                      padding: '10px 20px', cursor: 'pointer',
                    }}
                  >
                    + Tilføj nøgle / adgangskort
                  </button>
                </div>
              ) : (
                <>
                  {/* Table header */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 180px 90px 36px',
                    gap: 0, padding: '0 18px 8px',
                    fontSize: 11, fontWeight: 600, color: '#9ca3af',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    <span>Type</span>
                    <span>Kode / navn</span>
                    <span style={{ textAlign: 'center' }}>Antal</span>
                    <span />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {report.keys.map(k => (
                      <KeyRow
                        key={k.id}
                        keyItem={k}
                        reportId={id!}
                        onDelete={() => removeKey(k.id)}
                        onUpdate={load}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Tab 4: Fraflytningsinfo (fraflytning only) ── */}
          {tab === 4 && report.type === 'fraflytning' && (
            <div>
              <SectionTitle>Fraflytningsinfo</SectionTitle>

              <Card title="Ny adresse">
                <Field label="Lejers nye adresse">
                  <input
                    style={inputSt}
                    defaultValue={report.renter_new_address ?? ''}
                    placeholder="Gade, nr., postnr. og by"
                    onBlur={e => patch({ renter_new_address: e.target.value || null })}
                  />
                </Field>
              </Card>

              <Card title="Bankoplysninger til depositumrefundering">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Field label="Reg. nr.">
                    <input
                      style={inputSt}
                      defaultValue={report.renter_bank_reg ?? ''}
                      placeholder="XXXX"
                      onBlur={e => patch({ renter_bank_reg: e.target.value || null })}
                    />
                  </Field>
                  <Field label="Kontonummer">
                    <input
                      style={inputSt}
                      defaultValue={report.renter_bank_account ?? ''}
                      placeholder="XXXXXXXXXX"
                      onBlur={e => patch({ renter_bank_account: e.target.value || null })}
                    />
                  </Field>
                </div>
              </Card>
            </div>
          )}

          {/* ── Last tab: Underskriv & send ── */}
          {tab === lastTab && (
            <div>
              <SectionTitle>Underskriv & send</SectionTitle>

              <Card title="Gennemse rapporten">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <SummaryLine label="Type" value={typeLabel} />
                  <SummaryLine label="Adresse" value={report.property_address ?? '—'} />
                  <SummaryLine
                    label="Lejer"
                    value={[report.renter_first_name, report.renter_last_name].filter(Boolean).join(' ') || '—'}
                  />
                  <SummaryLine
                    label="Rum gennemgået"
                    value={`${report.rooms.filter(r => r.completed).length} / ${report.rooms.length}`}
                  />
                  <SummaryLine
                    label="Anmærkninger"
                    value={String(report.rooms.reduce((acc, r) => acc + r.remark_count, 0))}
                  />
                  <SummaryLine label="Forbrugsmålere" value={String(report.meters.length)} />
                  <SummaryLine label="Nøgler" value={String(report.keys.length)} last />
                </div>
              </Card>

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button
                  onClick={async () => {
                    try {
                      const { apiFetchRaw } = await import('../../lib/api-client')
                      const resp = await apiFetchRaw(`/api/v1/inspection-reports/${id}/pdf`)
                      if (!resp.ok) { alert('Kunne ikke hente PDF.'); return }
                      const blob = await resp.blob()
                      const url = URL.createObjectURL(blob)
                      window.open(url, '_blank')
                    } catch { alert('Kunne ikke hente PDF.') }
                  }}
                  style={{
                    padding: '12px 20px',
                    background: 'white',
                    color: '#374151',
                    border: '1px solid #e8eaed',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                  Vis PDF
                </button>
                <button
                  onClick={async () => {
                    try {
                      await patch({ status: 'completed' })
                      alert('Rapport markeret som afsluttet. Digital signering sættes op i næste fase.')
                    } catch (e) {
                      // Server detail is Danish and explains why (e.g. missing syn date).
                      alert(e instanceof ApiError ? e.message : 'Rapporten kunne ikke afsluttes. Prøv igen.')
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: BLUE,
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Markér som afsluttet
                </button>
              </div>
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>
                Digital signering via MitID sættes op i næste fase.
              </p>
              <ComplianceDisclaimer />

            </div>
          )}

        </div>
      </div>

      {/* ── Nav footer ──────────────────────────────────────────── */}
      {!(tab === 1 && activeRoomId) && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 32px',
          background: 'white',
          borderTop: '1px solid #e8eaed',
          flexShrink: 0,
        }}>
          <button
            onClick={() => setTab(t => Math.max(0, t - 1))}
            disabled={tab === 0}
            style={{
              padding: '9px 20px',
              fontSize: 13,
              background: 'white',
              color: tab === 0 ? '#d1d5db' : '#374151',
              border: '1px solid #e8eaed',
              borderRadius: 8,
              cursor: tab === 0 ? 'default' : 'pointer',
            }}
          >
            ← Forrige
          </button>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            Trin {tab + 1} af {TABS.length}
          </span>
          {tab < lastTab ? (
            <button
              onClick={() => setTab(t => t + 1)}
              style={{
                padding: '9px 24px',
                fontSize: 13,
                fontWeight: 600,
                background: BLUE,
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Næste →
            </button>
          ) : (
            <div style={{ width: 100 }} />
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {showMeterPicker && (
        <TypePickerModal
          title="Vælg målertype"
          types={METER_TYPES}
          onSelect={addMeterOfType}
          onClose={() => setShowMeterPicker(false)}
        />
      )}
      {showKeyPicker && (
        <TypePickerModal
          title="Vælg nøgletype"
          types={KEY_TYPES}
          onSelect={addKeyOfType}
          onClose={() => setShowKeyPicker(false)}
        />
      )}
    </div>
  )
}

// ─── TypePickerModal ──────────────────────────────────────────────────────────

function TypePickerModal({
  title, types, onSelect, onClose,
}: {
  title: string; types: string[]; onSelect: (t: string) => void; onClose: () => void
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.32)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200,
    }}>
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: '24px 28px',
        width: 400,
        boxShadow: '0 16px 48px rgba(0,0,0,0.16)',
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 18 }}>
          {title}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {types.map(t => (
            <button
              key={t}
              onClick={() => onSelect(t)}
              style={{
                padding: '11px 14px',
                fontSize: 13,
                textAlign: 'left',
                background: '#f7f8fa',
                border: '1px solid #e8eaed',
                borderRadius: 8,
                cursor: 'pointer',
                color: '#111827',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = BLUE; e.currentTarget.style.background = '#eef2ff' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8eaed'; e.currentTarget.style.background = '#f7f8fa' }}
            >
              {t}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9,18 15,12 9,6" />
              </svg>
            </button>
          ))}
        </div>
        <button
          onClick={onClose}
          style={{
            marginTop: 14,
            width: '100%',
            padding: '11px',
            fontSize: 13,
            background: 'white',
            border: '1px solid #e8eaed',
            borderRadius: 8,
            cursor: 'pointer',
            color: '#6b7280',
          }}
        >
          Annuller
        </button>
      </div>
    </div>
  )
}

// ─── RoomDetailView ───────────────────────────────────────────────────────────

function RoomDetailView({
  reportId, roomId, room, onBack,
}: {
  reportId: string; roomId: string; room: Room; onBack: () => void
}) {
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(true)
  const [showRemarkModal, setShowRemarkModal] = useState<string | null>(null)
  const [completed, setCompleted] = useState(room.completed)

  const loadItems = useCallback(async () => {
    setLoading(true)
    const data = await apiFetch<Item[]>(
      `/api/v1/inspection-reports/${reportId}/rooms/${roomId}/items`,
    )
    setItems(data.map(item => ({
      ...item,
      remarks: Array.isArray(item.remarks) ? item.remarks.map(r => ({
        ...r,
        photos: Array.isArray(r.photos) ? r.photos : [],
      })) : [],
    })))
    setLoading(false)
  }, [reportId, roomId])

  useEffect(() => { (async () => { await loadItems() })() }, [loadItems])

  const updateCondition = async (itemId: string, condition: string | null) => {
    await apiFetch(`/api/v1/inspection-reports/${reportId}/rooms/${roomId}/items/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ condition }),
    })
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, condition } : i))
  }

  const deleteRemark = async (itemId: string, remarkId: string) => {
    await apiFetch(
      `/api/v1/inspection-reports/${reportId}/rooms/${roomId}/items/${itemId}/remarks/${remarkId}`,
      { method: 'DELETE' },
    )
    setItems(prev => prev.map(i =>
      i.id === itemId ? { ...i, remarks: i.remarks.filter(r => r.id !== remarkId) } : i,
    ))
  }

  const markComplete = async () => {
    await apiFetch(`/api/v1/inspection-reports/${reportId}/rooms/${roomId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: true }),
    })
    setCompleted(true)
    onBack()
  }

  const ratedCount = items.filter(i => i.condition).length

  return (
    <div>
      {/* Back breadcrumb */}
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: '#6b7280',
          background: 'none', border: 'none', cursor: 'pointer',
          marginBottom: 18, padding: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15,18 9,12 15,6" />
        </svg>
        Tilbage til rum
      </button>

      {/* Room header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20,
      }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
            {room.name}
          </h2>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>
            {ratedCount} / {items.length} stande vurderet
          </div>
        </div>
        {completed ? (
          <span style={{
            fontSize: 12, color: '#16a34a', fontWeight: 600,
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            padding: '7px 14px', borderRadius: 8,
          }}>
            Rum fuldendt
          </span>
        ) : (
          <button
            onClick={markComplete}
            style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 600,
              background: '#f0fdf4', color: '#16a34a',
              border: '1px solid #bbf7d0', borderRadius: 8, cursor: 'pointer',
            }}
          >
            Markér fuldendt
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#9ca3af', fontSize: 13 }}>Indlæser enheder…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(item => (
            <div
              key={item.id}
              style={{
                background: 'white',
                border: '1px solid #e8eaed',
                borderRadius: 10,
                padding: '14px 18px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}
            >
              {/* Item name */}
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {item.name}
              </div>

              {/* Condition pills */}
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
                {CONDITIONS.map(c => {
                  const selected = item.condition === c.value
                  return (
                    <button
                      key={c.value}
                      onClick={() => updateCondition(item.id, selected ? null : c.value)}
                      style={{
                        height: 30,
                        padding: '0 11px',
                        fontSize: 12,
                        fontWeight: 600,
                        background: selected ? c.color : 'white',
                        color: selected ? 'white' : '#6b7280',
                        border: `1px solid ${selected ? c.color : '#d1d5db'}`,
                        borderRadius: 99,
                        cursor: 'pointer',
                        transition: 'background .1s, color .1s',
                      }}
                    >
                      {c.label}
                    </button>
                  )
                })}
              </div>

              {/* Remark cards */}
              {item.remarks.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                  {item.remarks.map(rem => (
                    <div
                      key={rem.id}
                      style={{
                        background: '#f9fafb',
                        border: '1px solid #e8eaed',
                        borderRadius: 8,
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#374151', lineHeight: '1.4' }}>
                          {rem.description}
                        </div>
                        {rem.who_pays && (
                          <span style={{
                            display: 'inline-block',
                            marginTop: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 99,
                            background: rem.who_pays === 'lejer' ? '#fef9c3' : '#dbeafe',
                            color: rem.who_pays === 'lejer' ? '#92400e' : '#1e40af',
                          }}>
                            {rem.who_pays === 'lejer' ? 'Lejer betaler' : 'Udlejer betaler'}
                          </span>
                        )}
                        {rem.photos.length > 0 && (
                          <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
                            {rem.photos.map(ph => (
                              <AuthImg key={ph.id} src={ph.url} style={{
                                width: 64, height: 64, borderRadius: 6,
                                border: '1px solid #e8eaed',
                              }} />
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => deleteRemark(item.id, rem.id)}
                        style={{
                          fontSize: 14, lineHeight: 1, color: '#d1d5db',
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: '0 2px', flexShrink: 0,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#d1d5db')}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add remark ghost button */}
              <button
                onClick={() => setShowRemarkModal(item.id)}
                style={{
                  fontSize: 12,
                  color: '#9ca3af',
                  background: 'none',
                  border: '1px dashed #e8eaed',
                  borderRadius: 6,
                  cursor: 'pointer',
                  padding: '5px 12px',
                  width: '100%',
                  textAlign: 'left',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = BLUE; e.currentTarget.style.color = BLUE }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8eaed'; e.currentTarget.style.color = '#9ca3af' }}
              >
                + Tilføj anmærkning
              </button>
            </div>
          ))}
        </div>
      )}

      {showRemarkModal && (
        <RemarkModal
          reportId={reportId}
          roomId={roomId}
          itemId={showRemarkModal}
          items={items}
          onClose={() => { setShowRemarkModal(null); loadItems() }}
        />
      )}
    </div>
  )
}

// ─── RemarkModal ──────────────────────────────────────────────────────────────

function RemarkModal({
  reportId, roomId, itemId, items, onClose,
}: {
  reportId: string; roomId: string; itemId: string; items: Item[]; onClose: () => void
}) {
  const item = items.find(i => i.id === itemId)
  const [step, setStep] = useState<'form' | 'photos'>('form')
  const [description, setDescription] = useState('')
  const [whoPays, setWhoPays] = useState<'lejer' | 'udlejer'>('lejer')
  const [saving, setSaving] = useState(false)
  const [savedRemarkId, setSavedRemarkId] = useState<string | null>(null)
  const [uploadedPhotos, setUploadedPhotos] = useState<Photo[]>([])
  const [uploading, setUploading] = useState(false)

  const save = async () => {
    if (!description.trim()) return
    setSaving(true)
    const { id: remarkId } = await apiFetch<{ id: string }>(
      `/api/v1/inspection-reports/${reportId}/rooms/${roomId}/items/${itemId}/remarks`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, who_pays: whoPays }),
      },
    )
    setSavedRemarkId(remarkId)
    setSaving(false)
    setStep('photos')
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !savedRemarkId) return
    setUploading(true)
    const photo = await uploadPhotoRaw(reportId, file, { remarkId: savedRemarkId })
    setUploadedPhotos(prev => [...prev, photo])
    setUploading(false)
    e.target.value = ''
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.32)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200,
    }}>
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: '26px 30px',
        width: 520,
        boxShadow: '0 16px 48px rgba(0,0,0,0.16)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          {step === 'form' ? 'Tilføj anmærkning' : 'Dokumentationsfoto'}
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>
          {item?.name}
        </div>

        {step === 'form' && (
          <>
            <Field label="Hvem skal betale?">
              <div style={{ display: 'flex', gap: 8 }}>
                {(['lejer', 'udlejer'] as const).map(opt => (
                  <button
                    key={opt}
                    onClick={() => setWhoPays(opt)}
                    style={{
                      flex: 1,
                      padding: '9px',
                      fontSize: 13,
                      fontWeight: whoPays === opt ? 600 : 400,
                      background: whoPays === opt ? '#eef2ff' : 'white',
                      border: `1.5px solid ${whoPays === opt ? BLUE : '#e8eaed'}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      color: whoPays === opt ? BLUE : '#374151',
                    }}
                  >
                    {opt === 'lejer' ? 'Lejer' : 'Udlejer'}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Beskrivelse">
              <textarea
                style={{ ...inputSt, minHeight: 96, resize: 'vertical' }}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Beskriv anmærkningen…"
                autoFocus
              />
            </Field>

            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '11px', fontSize: 13, background: 'white',
                  border: '1px solid #e8eaed', borderRadius: 8, cursor: 'pointer', color: '#374151',
                }}
              >
                Annuller
              </button>
              <button
                onClick={save}
                disabled={saving || !description.trim()}
                style={{
                  flex: 1, padding: '11px', fontSize: 13, fontWeight: 600,
                  background: description.trim() ? BLUE : '#e8eaed',
                  color: description.trim() ? 'white' : '#9ca3af',
                  border: 'none', borderRadius: 8,
                  cursor: description.trim() ? 'pointer' : 'default',
                }}
              >
                {saving ? 'Gemmer…' : 'Gem anmærkning'}
              </button>
            </div>
          </>
        )}

        {step === 'photos' && (
          <>
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 13,
              color: '#166534',
              marginBottom: 18,
            }}>
              Anmærkning gemt. Tilføj evt. dokumentationsfoto.
            </div>

            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8,
              marginBottom: 20, minHeight: 80,
            }}>
              {uploadedPhotos.map(ph => (
                <AuthImg key={ph.id} src={ph.url} style={{
                  width: 72, height: 72, borderRadius: 8, border: '1px solid #e8eaed',
                }} />
              ))}
              <label style={{
                width: 72, height: 72,
                border: '1.5px dashed #d1d5db',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                cursor: uploading ? 'wait' : 'pointer',
                color: '#9ca3af',
                fontSize: 10,
                background: '#f7f8fa',
              }}>
                <CameraIcon />
                {uploading ? 'Upload…' : 'Tag foto'}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
              </label>
            </div>

            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '11px', fontSize: 13, fontWeight: 600,
                background: BLUE, color: 'white', border: 'none',
                borderRadius: 8, cursor: 'pointer',
              }}
            >
              Færdig
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── MeterRow ─────────────────────────────────────────────────────────────────

function MeterRow({ meter, reportId, onDelete, onUpdate }: {
  meter: Meter; reportId: string; onDelete: () => void; onUpdate: () => void
}) {
  const [photos, setPhotos] = useState<Photo[]>(Array.isArray(meter.photos) ? meter.photos : [])
  const [uploading, setUploading] = useState(false)

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const photo = await uploadPhotoRaw(reportId, file, { meterId: meter.id })
    setPhotos(prev => [...prev, photo])
    setUploading(false)
    e.target.value = ''
  }

  return (
    <div style={{
      background: 'white',
      border: '1px solid #e8eaed',
      borderRadius: 10,
      padding: '14px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{meter.meter_type}</div>
        <button
          onClick={onDelete}
          style={{ fontSize: 12, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
          onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
        >
          Fjern
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="Målernummer">
          <input
            style={inputSt}
            defaultValue={meter.meter_number ?? ''}
            placeholder="Fx M12345"
            onBlur={async e => {
              await apiFetch(`/api/v1/inspection-reports/${reportId}/meters/${meter.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ meter_number: e.target.value || null }),
              }).catch(() => null)
            }}
          />
        </Field>
        <Field label="Aflæst måling">
          <input
            style={inputSt}
            type="number"
            defaultValue={meter.reading ?? ''}
            placeholder="Fx 1234.5"
            onBlur={async e => {
              await apiFetch(`/api/v1/inspection-reports/${reportId}/meters/${meter.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reading: e.target.value ? parseFloat(e.target.value) : null }),
              }).catch(() => null)
              onUpdate()
            }}
          />
        </Field>
      </div>

      {/* Photo strip */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        {photos.map(ph => (
          <AuthImg key={ph.id} src={ph.url} style={{
            width: 52, height: 52, borderRadius: 6, border: '1px solid #e8eaed',
          }} />
        ))}
        <label style={{
          width: 52, height: 52,
          border: '1.5px dashed #d1d5db',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: uploading ? 'wait' : 'pointer',
          color: '#9ca3af',
          background: '#f7f8fa',
        }}>
          <CameraIcon size={16} />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handlePhotoUpload}
            disabled={uploading}
          />
        </label>
      </div>
    </div>
  )
}

// ─── KeyRow ───────────────────────────────────────────────────────────────────

function KeyRow({ keyItem, reportId, onDelete, onUpdate }: {
  keyItem: Key; reportId: string; onDelete: () => void; onUpdate: () => void
}) {
  const updateCount = async (count: number) => {
    await apiFetch(`/api/v1/inspection-reports/${reportId}/keys/${keyItem.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ count }),
    }).catch(() => null)
    onUpdate()
  }

  return (
    <div style={{
      background: 'white',
      border: '1px solid #e8eaed',
      borderRadius: 8,
      padding: '11px 18px',
      display: 'grid',
      gridTemplateColumns: '1fr 180px 90px 36px',
      gap: 0,
      alignItems: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{keyItem.key_type}</div>

      <input
        style={{ ...inputSt, marginRight: 12 }}
        defaultValue={keyItem.key_code ?? ''}
        placeholder="Kode / nummer"
        onBlur={async e => {
          await apiFetch(`/api/v1/inspection-reports/${reportId}/keys/${keyItem.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key_code: e.target.value || null }),
          }).catch(() => null)
        }}
      />

      {/* +/- stepper */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => updateCount(Math.max(0, keyItem.count - 1))}
          style={{
            width: 28, height: 30,
            border: '1px solid #e8eaed',
            borderRadius: '6px 0 0 6px',
            background: '#f7f8fa',
            cursor: 'pointer',
            fontSize: 16,
            color: '#374151',
          }}
        >−</button>
        <div style={{
          width: 36, height: 30,
          border: '1px solid #e8eaed',
          borderLeft: 'none', borderRight: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 500, color: '#111827',
        }}>
          {keyItem.count}
        </div>
        <button
          onClick={() => updateCount(keyItem.count + 1)}
          style={{
            width: 28, height: 30,
            border: '1px solid #e8eaed',
            borderRadius: '0 6px 6px 0',
            background: '#f7f8fa',
            cursor: 'pointer',
            fontSize: 16,
            color: '#374151',
          }}
        >+</button>
      </div>

      <button
        onClick={onDelete}
        style={{
          fontSize: 12, color: '#d1d5db', background: 'none',
          border: 'none', cursor: 'pointer', padding: 0,
          textAlign: 'center',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
        onMouseLeave={e => (e.currentTarget.style.color = '#d1d5db')}
      >
        ×
      </button>
    </div>
  )
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <h2 style={{
      fontSize: 18, fontWeight: 700, color: '#111827',
      margin: '0 0 20px', letterSpacing: '-0.2px',
      ...style,
    }}>
      {children}
    </h2>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e8eaed',
      borderRadius: 10,
      padding: '20px 24px',
      marginBottom: 14,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{
        fontSize: 12,
        fontWeight: 700,
        color: '#6b7280',
        marginBottom: 16,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        fontSize: 12, fontWeight: 600, color: '#374151',
        display: 'block', marginBottom: 5,
      }}>
        {label}
      </label>
      {children}
    </div>
  )
}

function SummaryLine({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '10px 0',
      borderBottom: last ? 'none' : '1px solid #f3f4f6',
    }}>
      <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{value}</span>
    </div>
  )
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function CameraIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  )
}

function HomeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9,22 9,12 15,12 15,22" />
    </svg>
  )
}

function KitchenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
    </svg>
  )
}

function BathIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
      <line x1="10" y1="5" x2="8" y2="7" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  )
}

function BedIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4v16" />
      <path d="M2 8h18a2 2 0 0 1 2 2v10" />
      <path d="M2 17h20" />
      <path d="M6 8v9" />
    </svg>
  )
}

function EntryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 4h3a2 2 0 0 1 2 2v14" />
      <path d="M2 20h3" />
      <path d="M13 20h9" />
      <path d="M10 12v.01" />
      <path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z" />
    </svg>
  )
}
